import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { ReactionArrowGlyph } from '../ReactionArrowGlyph';
import { MoleculeThumbnail } from '../MoleculeThumbnail';
import { DrawingThumbnail } from '../DrawingThumbnail';
import type { ResultEntry } from './searchResultsModel';
import type { PubChemMatch } from '@peruselab/api-client/moleculeSearch';

interface SearchSuggestionsListProps {
  readonly listboxId: string;
  readonly listboxLabel: string;
  readonly suggestions: readonly ResultEntry[];
  readonly activeIndex: number;
  readonly onSelect: (card: ResultEntry) => void;
  readonly onHover: (index: number) => void;
  // #465: rendered as one extra row after `suggestions`, at index `suggestions.length` -- no
  // MoleculeThumbnail (a PubChem hit has no locally-fetchable id to render one from), which is
  // itself half the "visually distinguishable from a local result" acceptance criterion.
  readonly pubchemFallback: PubChemMatch | null;
  readonly onSelectPubChem: (match: PubChemMatch) => void;
}

// The live "up to 5 suggestions" dropdown (#463) -- a role="listbox" the top-bar search
// <input role="combobox"> owns via aria-controls/aria-activedescendant. DOM focus stays on the
// input the whole time (the W3C APG "Combobox with List Autocomplete" pattern, not roving
// tabindex like RightPanel's tablist) so the user can keep typing while browsing; only the
// *virtual* focus (aria-selected / aria-activedescendant) moves with the arrow keys.
export function SearchSuggestionsList({
  listboxId,
  listboxLabel,
  suggestions,
  activeIndex,
  onSelect,
  onHover,
  pubchemFallback,
  onSelectPubChem
}: SearchSuggestionsListProps) {
  const { t } = useTranslation();
  // Plain <div>s, not <ul>/<li> -- both are ARIA-valid (screen readers key off the explicit
  // role, not the tag), but <ul>/<li> have their own default non-interactive roles that trip
  // static a11y linters expecting a <div>/<span>-based custom widget instead (a known false
  // positive for exactly this W3C APG pattern). Keyboard interaction lives entirely on the
  // combobox <input> (arrow keys move aria-activedescendant, Enter selects) -- individual
  // options intentionally carry no key handler of their own, matching the APG reference pattern.
  return (
    <div className="workspace-search-suggestions" role="listbox" id={listboxId} aria-label={listboxLabel}>
      {suggestions.map((card, index) => (
        // Keyboard interaction is owned entirely by the combobox <input> above (Enter selects
        // the active option) -- a per-option key handler would be redundant with, and could
        // conflict with, that.
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
        <div
          key={card.key}
          role="option"
          id={`${listboxId}-option-${index}`}
          aria-selected={index === activeIndex}
          // Programmatically focusable (satisfies "an option role must be focusable") without
          // joining the Tab order -- DOM focus deliberately never leaves the input; see above.
          tabIndex={-1}
          className={index === activeIndex ? 'workspace-search-suggestion active' : 'workspace-search-suggestion'}
          // Without this, the input's blur (which closes the popup) fires before the click,
          // so a pointer selection would never register -- see the ARIA combobox spec note.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelect(card)}
          onMouseEnter={() => onHover(index)}
        >
          {card.kind === 'reaction' ? (
            // #746: a reaction found by its own words -- its arrow for an icon, and its title.
            <>
              <span className="workspace-search-suggestion-reaction" aria-hidden="true">
                <ReactionArrowGlyph kind="forward" />
              </span>
              <span className="workspace-search-suggestion-label">{card.label || t('reactionResults.untitled')}</span>
              <span className="workspace-search-suggestion-badge">{t('workspace.search.reactionBadge')}</span>
            </>
          ) : card.kind === 'document' ? (
            <span className="workspace-search-suggestion-label">{card.label}</span>
          ) : card.kind === 'drawing' ? (
            <>
              <DrawingThumbnail drawingId={card.drawingId} version={card.updatedAt ?? undefined} size={32} />
              <span className="workspace-search-suggestion-label">{card.label || t('myDrawings.untitled')}</span>
            </>
          ) : (
            <>
              <MoleculeThumbnail moleculeId={card.thumbnailMoleculeId} size={32} />
              <span className="workspace-search-suggestion-label">{card.label}</span>
            </>
          )}
        </div>
      ))}
      {pubchemFallback ? (
        // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
        <div
          role="option"
          id={`${listboxId}-option-${suggestions.length}`}
          aria-selected={activeIndex === suggestions.length}
          tabIndex={-1}
          className={
            activeIndex === suggestions.length
              ? 'workspace-search-suggestion workspace-search-suggestion-pubchem active'
              : 'workspace-search-suggestion workspace-search-suggestion-pubchem'
          }
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onSelectPubChem(pubchemFallback)}
          onMouseEnter={() => onHover(suggestions.length)}
        >
          <Icon name="openInNew" className="workspace-search-suggestion-pubchem-icon" />
          <span className="workspace-search-suggestion-label">{pubchemFallback.label}</span>
          <span className="workspace-search-suggestion-badge">{t('workspace.search.pubchemBadge')}</span>
        </div>
      ) : null}
    </div>
  );
}
